import { FC, useMemo } from "react";
import { Box, Grid, Paper, Typography } from "@mui/material";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	Line,
	LineChart,
	Legend,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { IndependentRatingsPayload } from "../../../../../services/reports.service";

interface IndependentRatingsViewProps {
	payload: IndependentRatingsPayload;
}

const TONES = ["#1D9E75", "#F2A93B", "#D85A30", "#7F77DD", "#3683C2"];

const IndependentRatingsView: FC<IndependentRatingsViewProps> = ({
	payload,
}) => {
	const { distressField, emojiField, avgDistressByDay, emojiCountsByDay } =
		payload;

	const distressData = useMemo(
		() =>
			avgDistressByDay.map((d) => ({
				day: d.day,
				avgDistress: d.avgDistress,
			})),
		[avgDistressByDay],
	);

	// Flatten emoji counts into one row per day with each value as a key.
	const emojiData = useMemo(
		() =>
			emojiCountsByDay.map((d) => {
				const row: Record<string, number | string> = { day: d.day };
				for (const value of emojiField.values) {
					row[value] = d.counts[value] ?? 0;
				}
				return row;
			}),
		[emojiCountsByDay, emojiField.values],
	);

	const hasDistress = distressData.some((d) => d.avgDistress !== null);
	const hasEmoji = emojiCountsByDay.some((d) =>
		Object.values(d.counts).some((c) => c > 0),
	);

	if (!hasDistress && !hasEmoji) {
		return (
			<Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
				<Typography variant="body1" color="text.secondary">
					No submissions yet for this event in the selected window.
				</Typography>
			</Paper>
		);
	}

	return (
		<Grid container spacing={2}>
			<Grid item xs={12} md={6}>
				<Paper variant="outlined" sx={{ p: 2 }}>
					<Typography variant="subtitle2" gutterBottom>
						Distress over days (avg, scale {distressField.min}–
						{distressField.max})
					</Typography>
					<Box sx={{ width: "100%", height: 300 }}>
						<ResponsiveContainer width="100%" height="100%">
							<LineChart
								data={distressData}
								margin={{ top: 12, right: 24, left: 12, bottom: 8 }}
							>
								<CartesianGrid strokeDasharray="3 3" />
								<XAxis
									dataKey="day"
									type="number"
									domain={[1, 14]}
									ticks={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14]}
								/>
								<YAxis domain={[distressField.min, distressField.max]} />
								<Tooltip
									formatter={(value) =>
										typeof value === "number" ? value.toFixed(1) : "—"
									}
									labelFormatter={(label) => `Day ${label}`}
								/>
								<Line
									type="monotone"
									dataKey="avgDistress"
									stroke="#7F77DD"
									strokeWidth={2}
									connectNulls={false}
									isAnimationActive={false}
								/>
							</LineChart>
						</ResponsiveContainer>
					</Box>
				</Paper>
			</Grid>

			<Grid item xs={12} md={6}>
				<Paper variant="outlined" sx={{ p: 2 }}>
					<Typography variant="subtitle2" gutterBottom>
						Emoji distribution by day (count)
					</Typography>
					<Box sx={{ width: "100%", height: 300 }}>
						<ResponsiveContainer width="100%" height="100%">
							<BarChart
								data={emojiData}
								margin={{ top: 12, right: 24, left: 12, bottom: 8 }}
							>
								<CartesianGrid strokeDasharray="3 3" />
								<XAxis dataKey="day" />
								<YAxis allowDecimals={false} />
								<Tooltip />
								<Legend />
								{emojiField.values.map((value, idx) => (
									<Bar
										key={value}
										dataKey={value}
										stackId="emoji"
										name={value}
										isAnimationActive={false}
									>
										<Cell fill={TONES[idx % TONES.length]} />
									</Bar>
								))}
							</BarChart>
						</ResponsiveContainer>
					</Box>
				</Paper>
			</Grid>
		</Grid>
	);
};

export default IndependentRatingsView;
